import type { UnpublishModalProps } from "@editor/components/AppModalTypes";

import React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useModal } from "@editor/hooks/useModal";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import { invalidateResourcesRelatedToDeployment } from "@/features/next-gen/components/invalidateResourcesRelatedToDeployment";
import InlineAlert from "@replo/design-system/components/alert/InlineAlert";
import { successToast } from "@replo/design-system/components/alert/Toast";
import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { ExternalLink } from "lucide-react";

import usePublishedSiteUrls from "../../hooks/usePublishedSiteUrls";

export const UnpublishModal: React.FC<UnpublishModalProps> = (props) => {
  const projectId = useCurrentProjectId();
  const { closeModal } = useModal();

  const filesToUnpublish = React.useMemo(() => {
    if (props.type === "page") {
      return props.page.lastPublishedFileVersionId ? [props.page] : [];
    } else {
      return props.funnel.funnelSteps
        .map((step) => step.file)
        .filter((file): file is NonNullable<typeof file> =>
          Boolean(file?.id && file?.lastPublishedFileVersionId),
        );
    }
  }, [props]);

  const publishedPageCount = filesToUnpublish.length;

  const { mutate: unpublish } = trpc.v2ShopDeployment.deploy.useMutation({
    onMutate: async ({ fileIdsToUnpublish }) => {
      if (!projectId) {
        return;
      }

      let previousFunnel;
      let previousFunnelList;

      // Only update funnel caches if we're unpublishing a funnel
      if (props.type === "funnel") {
        await trpcUtils.funnel.find.cancel({
          projectId,
          funnelId: props.funnel.id,
        });

        previousFunnel = trpcUtils.funnel.find.getData({
          projectId,
          funnelId: props.funnel.id,
        });

        // NOTE (Cole, 2025-07-21): Optimistically update the funnel.find cache - set lastPublishedFileVersionId to null for unpublished files
        trpcUtils.funnel.find.setData(
          { projectId, funnelId: props.funnel.id },
          (oldFunnel) => {
            if (!oldFunnel) {
              return oldFunnel;
            }

            return {
              ...oldFunnel,
              funnelSteps: oldFunnel.funnelSteps.map((step) => {
                if (
                  step.file?.id &&
                  fileIdsToUnpublish.includes(step.file.id)
                ) {
                  return {
                    ...step,
                    file: {
                      ...step.file,
                      lastPublishedFileVersionId: null,
                    },
                  };
                }
                return step;
              }),
            };
          },
        );

        await trpcUtils.funnel.list.cancel({
          projectId,
          includeFiles: true,
        });

        previousFunnelList = trpcUtils.funnel.list.getData({
          projectId,
          includeFiles: true,
        });

        // NOTE (Cole, 2025-07-21): Optimistically update the funnel.list cache - set lastPublishedFileVersionId to null for unpublished files
        trpcUtils.funnel.list.setData(
          { projectId, includeFiles: true },
          (oldFunnelList) => {
            if (!oldFunnelList) {
              return oldFunnelList;
            }

            return oldFunnelList.map((funnel) => {
              if (funnel.id === props.funnel.id) {
                return {
                  ...funnel,
                  funnelSteps: funnel.funnelSteps.map((step) => {
                    if (
                      step.file?.id &&
                      fileIdsToUnpublish.includes(step.file.id)
                    ) {
                      return {
                        ...step,
                        file: {
                          ...step.file,
                          lastPublishedFileVersionId: null,
                        },
                      };
                    }
                    return step;
                  }),
                };
              }
              return funnel;
            });
          },
        );
      }

      // NOTE (Cole, 2025-07-21): Also optimistically update individual page component caches
      const previousPageCaches: Record<string, any> = {};

      fileIdsToUnpublish.forEach((fileId) => {
        void trpcUtils.nextJSProject.getPage.cancel({
          projectId,
          id: fileId,
        });

        const previousPageData = trpcUtils.nextJSProject.getPage.getData({
          projectId,
          id: fileId,
        });

        if (previousPageData) {
          previousPageCaches[fileId] = previousPageData;

          trpcUtils.nextJSProject.getPage.setData(
            { projectId, id: fileId },
            {
              ...previousPageData,
              lastPublishedFileVersionId: null,
            },
          );
        }
      });

      return { previousFunnel, previousFunnelList, previousPageCaches };
    },
    onError: (_err, { fileIdsToUnpublish }, context) => {
      if (context?.previousFunnel && projectId && props.type === "funnel") {
        trpcUtils.funnel.find.setData(
          { projectId, funnelId: props.funnel.id },
          context.previousFunnel,
        );
      }

      if (context?.previousFunnelList && projectId && props.type === "funnel") {
        trpcUtils.funnel.list.setData(
          { projectId, includeFiles: true },
          context.previousFunnelList,
        );
      }

      if (context?.previousPageCaches && projectId) {
        fileIdsToUnpublish.forEach((fileId) => {
          const previousPageData = context.previousPageCaches[fileId];
          if (previousPageData) {
            trpcUtils.nextJSProject.getPage.setData(
              { projectId, id: fileId },
              previousPageData,
            );
          }
        });
      }
    },
    onSettled: (_data, _error, { fileIdsToUnpublish }) => {
      if (props.type === "funnel") {
        void trpcUtils.funnel.find.invalidate({
          projectId,
          funnelId: props.funnel.id,
        });
        void trpcUtils.funnel.list.invalidate({
          projectId,
          includeFiles: true,
        });
      }

      if (projectId) {
        fileIdsToUnpublish.forEach((fileId) => {
          void trpcUtils.nextJSProject.getPage.invalidate({
            projectId,
            id: fileId,
          });
        });
      }

      invalidateResourcesRelatedToDeployment();
    },
  });

  const publishedSiteUrls = usePublishedSiteUrls({
    pageRoutesToUseForPreview: (() => {
      if (props.type === "funnel") {
        return props.funnel.funnelSteps
          .map((step) => {
            return step.file?.pageRoute;
          })
          .filter((route): route is string => Boolean(route));
      } else if (props.page.pageRoute) {
        return [props.page.pageRoute];
      } else {
        return [];
      }
    })(),
  });

  if (!projectId) {
    return null;
  }

  const handleUnpublish = () => {
    void unpublish({
      projectId,
      fileIdsToPublish: [],
      fileIdsToUnpublish: filesToUnpublish.map((file) => file.id),
    });

    const itemType = props.type === "page" ? "Page" : "Pages";
    successToast(
      `${itemType} Unpublished`,
      `${publishedPageCount} page${publishedPageCount !== 1 ? "s have" : " has"} been unpublished - wait a few minutes to see changes to your live site.`,
    );

    closeModal({ type: "unpublishModal" });
  };

  const modalTitle =
    props.type === "page"
      ? "Unpublish Page"
      : `Unpublish ${publishedPageCount} Page${publishedPageCount !== 1 ? "s" : ""}`;

  const alertText =
    props.type === "page"
      ? "This page will no longer be live. Visitors will see a 404 page if they access its URL."
      : "These pages will no longer be live. Visitors will see a 404 page if they access their URLs.";

  return (
    <Modal
      title={modalTitle}
      isOpen={true}
      onOpenChange={(open) => {
        if (!open) {
          closeModal({ type: "unpublishModal" });
        }
      }}
      size="sm"
      footer={
        <div className="flex flex-row gap-2">
          <Button
            variant="secondary"
            size="base"
            onClick={() => closeModal({ type: "unpublishModal" })}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            size="base"
            onClick={handleUnpublish}
            data-testid="unpublish-funnel-button"
          >
            Unpublish
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          {filesToUnpublish.map((file, index) => {
            return (
              <div
                key={file.id}
                className="flex items-center justify-between p-3 border border-border rounded-md bg-background"
              >
                <div className="flex flex-col gap-1">
                  <div className="header-h4">
                    {file.name || "Untitled Page"}
                  </div>
                  <div className="text-sm text-placeholder">
                    {file.pageRoute ?? "/"}
                  </div>
                </div>
                <IconButton
                  icon={<ExternalLink size={12} />}
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    if (publishedSiteUrls[index]) {
                      window.open(publishedSiteUrls[index], "_blank");
                    }
                  }}
                />
              </div>
            );
          })}
        </div>

        <InlineAlert variant="warning">{alertText}</InlineAlert>
      </div>
    </Modal>
  );
};
